/**
 * Pure path-matching and recording-scope decisions for session replay and DOM
 * snapshots, extracted from the SDK class so these privacy rules can be
 * unit-tested (and mutation-tested) without a DOM or the rrweb recorder, in the
 * same spirit as sessionLifecycle.ts.
 *
 * Why a SEPARATE pair of lists from allowedPaths/disallowedPaths
 * -------------------------------------------------------------
 * allowedPaths/disallowedPaths gate INTERACTION capture (clicks, scrolls,
 * heatmap events). Existing properties have configured them, often narrowly,
 * for heatmaps. Reusing them to gate recording would silently switch replay off
 * for those customers, so recording gets its own pair: recordingAllowedPaths /
 * recordingDisallowedPaths.
 *
 * The one place "backwards compatible" and "fail closed" pull apart
 * ----------------------------------------------------------------
 * When a property has configured NEITHER recording list, recording is allowed on
 * EVERY path. That is deliberate: it preserves exactly today's behavior for any
 * property that has not opted in (backwards compatible; fail OPEN on absence of
 * configuration). But the MOMENT a property configures either list, the rules
 * become strict and anything not clearly allowed is excluded (fail CLOSED). A
 * future reader will otherwise assume the code is one policy or the other; it is
 * intentionally BOTH, switched on whether the property has opted in. See
 * isRecordingAllowedForPath.
 */

/**
 * Canonicalize a URL path (or a user-entered pattern) for matching:
 *  - drop any query string ("?...") and hash fragment ("#..."),
 *  - ensure exactly one leading slash, so "account" and "/account" are equal,
 *  - drop a single trailing slash except on root ("/account/" == "/account",
 *    but "/" stays "/").
 * Matching is case-sensitive, consistent with how servers route URL paths.
 */
export function normalizeRecordingPath(input: string): string {
  if (typeof input !== "string") return "/";
  let p = input.split("#")[0].split("?")[0].trim();
  if (p === "") return "/";
  // A leading "*" pattern (e.g. "*" or "*/foo") is left as-is; otherwise force
  // a single leading slash.
  if (p[0] !== "/" && p[0] !== "*") p = "/" + p;
  // Drop one trailing slash, but never reduce "/" to "".
  if (p.length > 1 && p[p.length - 1] === "/") p = p.slice(0, -1);
  return p;
}

/**
 * Does a normalized path match a single pattern?
 *  - "*" in the pattern is a wildcard matching any run of characters, INCLUDING
 *    "/", so "/orders/*" matches "/orders/123" and "/orders/123/items", and a
 *    bare "*" matches every path.
 *  - everything else is literal, so "/pricing" matches only "/pricing".
 * There is no ":param" syntax; use "*" for dynamic segments ("/orders/*").
 * Note "/app/*" matches paths UNDER /app but not "/app" itself; use "/app*" to
 * cover a section and everything beneath it.
 */
export function matchRecordingPath(path: string, pattern: string): boolean {
  const p = normalizeRecordingPath(path);
  const pat = normalizeRecordingPath(pattern);
  if (pat === "*" || pat === "/*") return true;
  if (pat.indexOf("*") === -1) return p === pat;
  const escaped = pat
    .split("*")
    .map((seg) => seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join(".*");
  return new RegExp("^" + escaped + "$").test(p);
}

/**
 * Whether session replay / DOM snapshots may be captured on `pathname`, given a
 * property's recording path lists. THIS is the fail-open-when-unconfigured /
 * fail-closed-when-configured switch described in the module header.
 *
 *  - both lists empty/absent   -> true  (record everywhere; today's behavior)
 *  - a disallow match          -> false (disallow always wins)
 *  - allow list non-empty      -> true only if the path matches it; otherwise
 *                                 false (strict: not clearly allowed => excluded)
 *  - allow list empty but a
 *    disallow list present      -> true for anything not disallowed
 *                                 (deny-list-only mode: "record all except")
 */
export function isRecordingAllowedForPath(
  pathname: string,
  recordingAllowedPaths: string[] | null | undefined,
  recordingDisallowedPaths: string[] | null | undefined
): boolean {
  const allow = Array.isArray(recordingAllowedPaths)
    ? recordingAllowedPaths
    : [];
  const disallow = Array.isArray(recordingDisallowedPaths)
    ? recordingDisallowedPaths
    : [];

  // Unconfigured: fail OPEN so existing properties are unaffected.
  if (allow.length === 0 && disallow.length === 0) return true;

  // Disallow always wins.
  if (disallow.some((pat) => matchRecordingPath(pathname, pat))) return false;

  // Deny-list-only: everything not disallowed is allowed.
  if (allow.length === 0) return true;

  // Allow list present: strict. Not clearly allowed => excluded (fail CLOSED).
  return allow.some((pat) => matchRecordingPath(pathname, pat));
}

/**
 * Whether session replay may run right now, combining the three controls:
 *  - recordingEnabled: the server's master switch for the property,
 *  - pathAllowed:      the current path is within the recording scope,
 *  - appRecordable:    the app has not called pauseRecording().
 *
 * PRECEDENCE RULE (privacy-critical): all three must be true. Because they are
 * AND-ed, app code (appRecordable) can only ever make recording MORE
 * restrictive; it can never force recording where the SERVER disabled it or the
 * path scope excludes it. resumeRecording() therefore cannot defeat a
 * server-side privacy setting. Do not turn any of these into an OR.
 */
export function effectiveRecordable(
  recordingEnabled: boolean,
  pathAllowed: boolean,
  appRecordable: boolean
): boolean {
  return recordingEnabled && pathAllowed && appRecordable;
}

/**
 * Whether a DOM snapshot (the heatmap background) may be captured right now.
 * Independent of recordingEnabled, because the snapshot serves the heatmap
 * background even when session replay is off; but it still honors the path scope
 * and an app pause. Same one-directional precedence: app code only restricts.
 */
export function canCaptureDom(
  pathAllowed: boolean,
  appRecordable: boolean
): boolean {
  return pathAllowed && appRecordable;
}

export type RecordingNavEffect = "none" | "stop-and-discard" | "restart";

export interface RecordingNavState {
  recordingEvents: unknown[];
  pendingDomSnapshot: unknown | null;
  recorderRunning: boolean;
}

export interface RecordingNavResult {
  state: RecordingNavState;
  effect: RecordingNavEffect;
}

/**
 * Decide what happens to the recorder and its in-memory buffer when an SPA
 * navigation changes whether the current path is recordable. Pure so the
 * boundary behavior can be tested without a DOM or rrweb.
 *
 *  - crossing INTO an excluded path -> "stop-and-discard": stop the recorder and
 *    throw away the buffer (recordingEvents AND any pending DOM snapshot) so a
 *    buffer never spans the boundary and contains events from both sides.
 *  - crossing OUT to an allowed path (recording enabled) -> "restart": start a
 *    fresh recorder. rrweb emits a new FullSnapshot on start, which is required
 *    because the previous snapshot was discarded on the way in.
 *  - no boundary crossed, or recording disabled entirely -> "none".
 */
export function applyRecordingNav(
  state: RecordingNavState,
  prevAllowed: boolean,
  nextAllowed: boolean,
  recordingEnabled: boolean
): RecordingNavResult {
  if (!recordingEnabled || prevAllowed === nextAllowed) {
    return { state, effect: "none" };
  }
  if (!nextAllowed) {
    return {
      state: {
        recordingEvents: [],
        pendingDomSnapshot: null,
        recorderRunning: false,
      },
      effect: "stop-and-discard",
    };
  }
  return {
    state: {
      recordingEvents: [],
      pendingDomSnapshot: state.pendingDomSnapshot,
      recorderRunning: true,
    },
    effect: "restart",
  };
}
